import axios, { AxiosError, type AxiosRequestConfig } from "axios"

import i18n from "@/i18n"
import { env } from "@/config/env"
import {
  clearTokens,
  readTokens,
  writeTokens,
} from "@/lib/storage/auth-storage"
import type { BaseResponse } from "@/types/api"

interface TokenPair {
  accessToken: string
  refreshToken: string
  expiresIn: number
  refreshExpiresIn: number
  tokenType: string
}

const REFRESH_PATH = "/auth/exporter/refresh"
const LOGIN_PATH = "/auth/exporter/login"
const REFRESH_LEEWAY_MS = 60_000

type AuthEvent = "logout"
type AuthListener = (event: AuthEvent) => void
const authListeners = new Set<AuthListener>()

export function subscribeAuth(listener: AuthListener) {
  authListeners.add(listener)
  return () => {
    authListeners.delete(listener)
  }
}

function emitAuth(event: AuthEvent) {
  authListeners.forEach((listener) => listener(event))
}

export const api = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
})

let refreshInFlight: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) {
    return refreshInFlight
  }

  const tokens = readTokens()
  if (!tokens?.refreshToken) {
    return null
  }

  refreshInFlight = (async () => {
    try {
      const { data } = await axios.post<BaseResponse<TokenPair>>(
        `${env.apiBaseUrl}${REFRESH_PATH}`,
        { refreshToken: tokens.refreshToken },
        { headers: { "Content-Type": "application/json" } }
      )

      const result = data.result
      if (!data.success || !result) {
        clearTokens()
        emitAuth("logout")
        return null
      }

      writeTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresIn: result.expiresIn,
      })
      return result.accessToken
    } catch {
      clearTokens()
      emitAuth("logout")
      return null
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

api.interceptors.request.use(async (config) => {
  const url = config.url ?? ""
  const isAuthEndpoint = url.includes(LOGIN_PATH) || url.includes(REFRESH_PATH)
  if (isAuthEndpoint) {
    return config
  }

  const tokens = readTokens()
  if (!tokens) {
    return config
  }

  let accessToken = tokens.accessToken
  if (tokens.accessExpiresAt - Date.now() < REFRESH_LEEWAY_MS) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      accessToken = refreshed
    }
  }

  config.headers = config.headers ?? {}
  config.headers.Authorization = `Bearer ${accessToken}`
  return config
})

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined
    const status = error.response?.status

    if (!original || status !== 401 || original._retried) {
      return Promise.reject(error)
    }

    const url = original.url ?? ""
    if (url.includes(LOGIN_PATH) || url.includes(REFRESH_PATH)) {
      return Promise.reject(error)
    }

    original._retried = true
    const newToken = await refreshAccessToken()
    if (!newToken) {
      return Promise.reject(error)
    }

    original.headers = original.headers ?? {}
    ;(original.headers as Record<string, string>).Authorization =
      `Bearer ${newToken}`
    return api.request(original)
  }
)

export function extractApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as BaseResponse | undefined
    if (data) {
      if (typeof data.error === "object" && data.error?.message) {
        return data.error.message
      }
      if (typeof data.error === "string") {
        return data.error
      }
      if (data.message) {
        return data.message
      }
    }
    return error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return i18n.t("errors.generic")
}

// Reads the stable error code (BaseResponse.res_code or .error.code)
// off an axios error or a thrown Error carrying a `.code` field.
// Returns undefined when no code is set — callers fall back to the
// raw message.
export function extractApiErrorCode(error: unknown): string | undefined {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as BaseResponse | undefined
    if (data) {
      if (data.res_code) return data.res_code
      if (typeof data.error === "object" && data.error?.code) {
        return data.error.code
      }
    }
    return undefined
  }
  // Errors thrown by `unwrap` (api/transit.api.ts) can carry a code in
  // a non-standard `.code` field; we don't currently use it but the
  // shape is here in case we want to in the future.
  if (error && typeof error === "object" && "code" in error) {
    const c = (error as { code?: unknown }).code
    if (typeof c === "string") return c
  }
  return undefined
}
